import React from 'react';
import { useLocation } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useTestingSessionStore } from '../../stores/testingSessionStore';

import { TESTING_CENTER_BUILD_ENABLED } from '../../testing-center/environment';

// The panel pulls in the generated suites JSON (~85 KB) — lazy-load it so the
// Testing Center costs nothing until a session is actually running. In a build
// with the flag off the import is dead code and the chunk is never emitted
// (PRX-121), so the overlay renders nothing.
const TestingOverlayPanel = TESTING_CENTER_BUILD_ENABLED
  ? React.lazy(() => import('./TestingOverlayPanel'))
  : null;

/** Routes where the overlay must never appear. */
const HIDDEN_PREFIXES = ['/login', '/callback', '/kiosk', '/unauthorized'];

/**
 * The panel chunk can fail to load (dev-server restart under a stale tab, a
 * redeploy replacing hashed chunks — same family as the "Unable to preload
 * CSS" Sentry issue). Without a local boundary that rejection reaches the
 * ROOT error boundary and white-screens all of PHOX — the optional QA overlay
 * must never take the host app down with it. A page refresh recovers
 * (React.lazy caches the rejection for the life of the page).
 */
class OverlayErrorBoundary extends React.Component<
  { onError: (error: unknown) => void; children: React.ReactNode },
  { failed: boolean }
> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error: unknown) {
    this.props.onError(error);
  }

  render() {
    return this.state.failed ? null : this.props.children;
  }
}

/**
 * Testing Center v1 — floating session overlay mount point.
 *
 * Mounted ONCE at the app layout level (App.tsx, next to FeedbackCapture) so
 * the pinned checklist survives navigation. Renders nothing unless a testing
 * session is active (session pointer lives in useTestingSessionStore,
 * persisted to sessionStorage so a reload restores it; the checkmarks
 * themselves re-hydrate from the server).
 */
export const TestingOverlay: React.FC = () => {
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const suiteId = useTestingSessionStore((s) => s.suiteId);
  const testNumber = useTestingSessionStore((s) => s.testNumber);

  if (!TestingOverlayPanel) return null;
  if (!suiteId || testNumber === null) return null;
  if (HIDDEN_PREFIXES.some((prefix) => location.pathname.startsWith(prefix))) return null;

  return (
    <OverlayErrorBoundary
      onError={(error) => {
        console.warn('[testing-center] overlay failed to load — refresh the page to restore it', error);
        enqueueSnackbar('The testing overlay failed to load — refresh the page to restore it.', {
          variant: 'warning',
          preventDuplicate: true,
        });
      }}
    >
      <React.Suspense fallback={null}>
        <TestingOverlayPanel />
      </React.Suspense>
    </OverlayErrorBoundary>
  );
};
